import {
    Button,
    FilterChip,
    FilterItem,
    FilterItemProps,
    getScrollbarClassName,
    IconArrowDown,
    IconArrowUp,
    IconRefresh,
    List,
    ListInputItem,
    ListItem,
    ListSection,
    ListStubItem,
    ListTreeItem,
    Popup,
    PopupProps,
    Text,
    TextInput,
    TextInputProps,
    TreeNode as TreeFilterNode,
    useCheckboxTreeState,
    useVStack,
    VStack,
} from '@direct-frontend/components';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { isObject, useDebounce, usePreviousState } from '@direct-frontend/stdlib';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { FilterValue } from '../../../filters/treeFilter/types';
import { countCheckedNodesWithoutCheckedChildren } from '../../../lib/helpers/countCheckedNodesWithoutCheckedChildren';
import { createNodeMap } from '../../../lib/helpers/createNodeMap';
import { FilterRenderProps, FilterVariablePart } from '../../../lib/types/Filters';
import { TestIdProps } from '../../../lib/types/TestIdProps';
import { HighlightedText } from '../../HighlightedText';
import { i18n } from './TreeQuickFilter.i18n';
import styles from './TreeQuickFilter.module.css';

/**
 * Пропсы фильтра дерева чекбоксов
 */
export type TreeQuickFilterProps = FilterVariablePart<FilterValue> &
    Pick<FilterRenderProps<FilterValue>, 'onChange'> &
    TestIdProps &
    Loggable & {
        /** Элементы дерева */
        items: TreeFilterNode[] | null;
        /** Название фильтра */
        title: string;
        /** Цвет фильтра */
        color?: 'contour' | 'contrast' | 'normal';
        /** Положение всплывающего элемента */
        placement?: PopupProps['placement'];
        /** флаг наличия ошибки */
        hasError?: boolean;
        /** Класс попапа */
        className?: string;
        /** Функция для повторного запроса данных */
        retry?: () => void;
        /** Размер фильтра */
        size?: FilterItemProps['size'];
        /** Оставлять ли место для каунтера, если нет выбранных фильтров */
        withBadgePlaceholder?: boolean;
    };

/**
 * Рендерер фильтра дерева чекбоксов
 */
export function TreeQuickFilter({
    logNode,
    'data-testid': dataTestId = 'TreeQuickFilter',
    items,
    value,
    color = 'normal',
    size = 'xs',
    placement = 'bottom-start',
    hasError,
    className,
    onChange,
    title,
    retry,
    withBadgePlaceholder,
}: TreeQuickFilterProps) {
    const [searchValue, setSearchValue] = useState('');
    // Признак наличия элементов при поиске
    const [isSearchSuccess, setIsSearchSuccess] = useState(true);

    const logNodeName = getLogNodeName({ logNode }) ?? 'TreeQuickFilter';

    // Стейт попапа
    const popupAnchorRef = useRef<HTMLDivElement>(null);
    const [isOpened, setIsOpened] = useState(false);
    const open = useCallback(() => {
        setIsOpened(true);
    }, []);
    const close = useCallback(() => {
        setIsOpened(false);
    }, []);

    const nodeMap = useMemo(() => createNodeMap(items ?? []), [items]);

    const count = useMemo(() => countCheckedNodesWithoutCheckedChildren(nodeMap, value), [nodeMap, value]);

    const { renderTree, setExpanded, collapseAll } = useCheckboxTreeState({
        nodes: items ?? [],
        initialCheckboxValue: false,
        changedNodes: value,
        setChangedNodes: update => {
            const newValue = update(value);
            onChange({ value: newValue });
        },
    });

    const itemsChanged = items !== usePreviousState(items);
    // Обработчик поиска
    const onSearch = useCallback(
        (searchValue: string) => {
            setSearchValue(searchValue);
            setIsSearchSuccess(true);
            if (!searchValue || !items) {
                return;
            }

            collapseAll();

            // Перебираем элементы дерева и раскрываем его если нашли нужный элемент
            function expandFoundNodes(node: TreeFilterNode): boolean {
                let found = false;
                if (node.children) {
                    for (const child of node.children) {
                        found = expandFoundNodes(child) || found;
                    }

                    if (found) {
                        setExpanded(node.id, true);
                    }
                }

                return node.label.toLowerCase().includes(searchValue.toLowerCase()) || found;
            }
            const found = items.map(expandFoundNodes).some(x => x);
            setIsSearchSuccess(found);
        },
        [collapseAll, items, setExpanded]
    );

    if (itemsChanged) {
        onSearch(searchValue);
    }

    const onReset = useCallback(() => {
        onChange({ value: [] });
    }, [onChange]);

    const isResetDisabled = value.length === 0;

    // Рендер дерева чекбоксов
    const renderItems = () => {
        if (!items) {
            return Array(5)
                .fill(0)
                .map((_, i) => <ListStubItem width="100%" key={i} />);
        }

        if (!isSearchSuccess) {
            return <ListItem logNode={`${logNodeName}.NoItems`}>{i18n('Значение фильтра не найдено')}</ListItem>;
        }

        return renderTree(({ level, setExpanded, isChecked, isExpanded, item, setChecked }) => {
            if (
                // Находимся в поиске,
                searchValue &&
                // элемент не раскрыт (не является родителем искомого элемента)
                !isExpanded &&
                // и сам не является искомым элементом
                !item.label.toLowerCase().includes(searchValue.toLowerCase())
            ) {
                return null;
            }
            return (
                <ListTreeItem
                    level={level}
                    hasExpandIcon={Boolean(item.children?.length)}
                    onChange={setChecked}
                    isSelected={isChecked}
                    onExpandChange={searchValue ? undefined : setExpanded}
                    isExpanded={isExpanded}
                    logNode={`${logNodeName}.TreeItem`}
                >
                    <HighlightedText text={item.label} search={searchValue} />
                </ListTreeItem>
            );
        });
    };

    const scrollClass = getScrollbarClassName();
    const stackClass = useVStack({});

    return (
        <>
            <FilterItem
                keyChip={
                    <ExpanderChip
                        data-testid={`${dataTestId}.ExpanderChip`}
                        count={count}
                        ref={popupAnchorRef}
                        expanded={isOpened}
                        close={close}
                        open={open}
                        title={title}
                        withBadgePlaceholder={withBadgePlaceholder}
                        logNode={`${logNodeName}.ExpanderChip`}
                    />
                }
                color={color}
                size={size}
                logNode={logNode}
            />
            <Popup
                className={`${styles.popup ?? ''} ${className ?? ''}`}
                data-testid={`${dataTestId}.Popup`}
                triggerRef={popupAnchorRef}
                placement={placement}
                isOpen={isOpened}
                onClose={close}
            >
                {hasError ? (
                    <ErrorContent retry={retry} />
                ) : (
                    <List size={size} logNode={`${logNodeName}.List`} width="max" className={stackClass}>
                        <ListSection>
                            <Search value={searchValue} onChange={onSearch} logNode={`${logNodeName}.Search`} />
                        </ListSection>
                        <ListSection>
                            <ListItem
                                isDisabled={isResetDisabled}
                                role="button"
                                onClick={onReset}
                                logNode={`${logNodeName}.Reset`}
                            >
                                {i18n('Сбросить')}
                            </ListItem>
                        </ListSection>
                        <ListSection />
                        <div className={`${styles.list ?? ''} ${scrollClass}`}>
                            <ListSection>{renderItems()}</ListSection>
                        </div>
                    </List>
                )}
            </Popup>
        </>
    );
}

function Search({ onChange, value, logNode }: { onChange: (value: string) => void; value: string } & Loggable) {
    const [searchValue, setSearchValue] = useState(value);

    // Устанавливаем значение поиска с задержкой чтобы не перерисовывать дерево на каждое изменение
    const onChangeDebounced = useDebounce(onChange, 300);

    const setValue = useCallback(
        (newValue: string) => {
            setSearchValue(oldValue => {
                if (newValue !== oldValue) {
                    onChangeDebounced(newValue.trim());
                }
                return newValue;
            });
        },
        [onChangeDebounced]
    );

    return (
        <ListInputItem
            placeholder={i18n('Поиск')}
            data-testid="TreeQuickFilterInput"
            value={searchValue}
            onChange={setValue}
        >
            {(props: TextInputProps) => <TextInput {...props} logNode={logNode} />}
        </ListInputItem>
    );
}

type ExpanderChipProps = TestIdProps &
    Loggable & {
        expanded: boolean;
        close: () => void;
        open: () => void;
        title: string;
        count: number;
        withBadgePlaceholder?: boolean;
    };

/**
 * Кнопка для открытия/закрытия попапа
 */
export const ExpanderChip = forwardRef<HTMLDivElement, ExpanderChipProps>(function ExpanderChip(props, forwardRef) {
    const {
        close,
        title,
        open,
        expanded,
        count,
        withBadgePlaceholder = true,
        'data-testid': dataTestId = 'ExpanderChip',
        logNode,
    } = props;

    const { logClick } = useLogger('ExpanderChip', logNode);

    const expand = useCallback(() => {
        open();
        logClick({
            type: 'openFilterPopup',
        });
    }, [open, logClick]);

    return (
        <FilterChip
            data-testid={dataTestId}
            ref={forwardRef}
            addonRight={
                <>
                    {withBadgePlaceholder && count === 0 && expanded ? (
                        <div className={styles.badgePlaceholder} />
                    ) : null}
                    {expanded ? <IconArrowUp color={'gray'} size="16" /> : <IconArrowDown color={'gray'} size="16" />}
                </>
            }
            counterValue={count}
            onClick={expanded ? close : expand}
            logNode={logNode}
        >
            {title}
        </FilterChip>
    );
});

/**
 * Содержимое попапа при ошибке загрузки данных
 * @param retry функция для повторной загрузки данных
 */
function ErrorContent({ retry }: { retry?: () => void }) {
    return (
        <VStack gap="16" alignItems="center" className={styles.errorContent} justifyContent="center">
            <Text size="l">{i18n('Что-то пошло не так')}</Text>
            {retry && (
                <Button
                    color="contour"
                    size="s"
                    data-testid="TreeQuickFilter.Retry"
                    onClick={retry}
                    logNode="retryButton"
                    iconLeft={IconRefresh}
                >
                    {i18n('Попробовать снова')}
                </Button>
            )}
        </VStack>
    );
}

function getLogNodeName({ logNode }: Loggable) {
    return logNode && isObject(logNode) ? logNode.name : logNode;
}
